/**
 * Tabs component
 */
export default class {
  /**
   * Tabs container
   *
   * @private
   *
   * @type {HTMLElement}
   */
  #container;

  /**
   * If scroll to opened tab is enabled
   *
   * @private
   *
   * @type {bool}
   */
  #useScroll = false;

  /**
   * List of tabs
   *
   * @private
   *
   * @type {NodeList}
   */
  #tabs;

  /**
   * Class to add to open a tab
   *
   * @private
   *
   * @type {string}
   */
  #className = 'open';

  /**
   * Time to wait for the end of the open / close animation in milliseconds
   *
   * @private
   *
   * @type {number}
   */
  #animationDelay = 650;

  /**
   * @param {HTMLElement} container Tabs container
   * @param {bool} useScroll If scroll to opened tab is enabled
   */
  constructor(container, useScroll = false) {
    this.#container = container;
    if (!this.#container) {
      throw new Error('CVRISK Tabs: container is not defined');
    }

    this.#useScroll = useScroll === true;
    this.#tabs = this.#container.querySelectorAll('.cvrisk-tab');

    // Initialize tabs
    if (this.#tabs.length > 0) {
      this.#init();
    }
  }

  /**
   * Open/Close a tab after click
   *
   * @param {Event} e Click event
   *
   * @return {void}
   */
  #handlerTabClick(e) {
    e.preventDefault();

    const tab = e.currentTarget.closest('.cvrisk-tab');
    if (!tab) {
      return;
    }

    if (this.isOpened(tab)) {
      this.close(tab);
    } else {
      this.open(tab);
    }
  }

  /**
   * Initialize component
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    this.#tabs.forEach((tab) => {
      // Open/Close on click
      tab
        .querySelector('.cvrisk-header-tab')
        .addEventListener('click', (e) => this.#handlerTabClick(e));

      // Scroll to opened tab
      tab.addEventListener('opened', () => this.scrollTo(tab));
    });
  }

  /**
   * If a tab is opened
   *
   * @param {HTMLElement} tab Tab element
   *
   * @return {bool}
   */
  isOpened(tab) {
    return tab.classList.contains(this.#className);
  }

  /**
   * If a tab is closed
   *
   * @param {HTMLElement} tab Tab element
   *
   * @returns {bool}
   */
  isClosed(tab) {
    return !this.isOpened(tab);
  }

  /**
   * Open a tab
   *
   * @param {HTMLElement} tab Tab element
   *
   * @return {void}
   */
  open(tab) {
    // wait for the animation to end before opening the tab
    setTimeout(
      () => {
        // Open tab
        tab.classList.add('open');

        // wait for the animation to end before sending 'opened' event
        setTimeout(
          // Dispatch 'opened' event
          () => tab.dispatchEvent(new CustomEvent('opened')),
          this.#animationDelay
        );
      },

      // Close opened tabs
      this.closeTabs() ? this.#animationDelay : 0
    );
  }

  /**
   * Close a tab
   *
   * @param {HTMLElement} tab Tab element
   *
   * @return {void}
   */
  close(tab) {
    // Close tab
    tab.classList.remove('open');

    // Dispatch 'closed' event
    tab.dispatchEvent(new CustomEvent('closed'));
  }

  /**
   * Close opened tabs
   *
   * @return {bool} If at least one tab has been closed
   */
  closeTabs() {
    let res = false;
    this.#tabs.forEach((tab) => {
      if (this.isOpened(tab)) {
        this.close(tab);
        res = true;
      }
    });

    return res;
  }

  /**
   * Scroll to tab
   *
   * @param {HTMLElement} tab Tab element
   *
   * @return {void}
   */
  scrollTo(tab) {
    if (!this.#useScroll) {
      return;
    }

    let top = tab.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: top,
      left: 0,
      behavior: 'smooth',
    });
  }

  /**
   * Get tabs
   *
   * @return {NodeList}
   */
  get tabs() {
    return this.#tabs;
  }
}
